import { useState, type ReactNode } from 'react';
import type { HomeTone } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import type { MemberCtx } from './MemberHome';
import { facts } from './Sheet';
import { Act, chipC, D, DASH, S } from './ui';
import { HealthWatch } from './HealthWatch';
import { BuildLabel, useBuildLabels, useCustomerText } from '../../ui/buildLabels';

// My home (design T11246-11485, homeVals hTabHome). The score and its five drivers read
// the real GET /residents/:id/score, the memory checks the real GET /cognitive and the
// medicines the real GET /medications. The forecast, the one thing, the trackers, the
// weight and strength record, "Already settled" and the four pillars wait on the
// PROPOSED GET /athome/me/:id/wellbeing.

const CARD = 'grid-column:1/-1;box-sizing:border-box;min-width:0;padding:20px 22px;border-radius:18px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);';
const GRADE: Record<string, string> = { A: '#1F6B52', B: '#2F5C97', C: '#5B6B7C', D: '#B3261E', F: '#B3261E' };
/** The member's own words for the five score domains (the design's D list). */
const DOMAIN_WORDS: Record<string, string> = {
  mood_affect: 'How you have been feeling',
  cognition: 'Memory and thinking',
  mobility_fall_risk: 'Steadiness',
  medication_load: 'How many medicines',
  engagement: 'Keeping in touch',
};
const TRK: Record<HomeTone, string> = { good: '#1F6B52', watch: '#5B6B7C', none: '#54606C', alert: '#B3261E' };
const PATH = (t: HomeTone | 'base') => (t === 'base' ? '#2F5C97' : t === 'watch' ? '#5B6B7C' : t === 'alert' ? '#B3261E' : '#2F8A6F');
const DARK: Record<string, string> = { '#1F6B52': '#12503C', '#2F5C97': '#264C7E', '#54606C': '#3C454F' };
/** The design's member chip(fg, bg, bd). */
const mchip = (fg: string, bg: string, bd: string) =>
  'font-size:16px;font-weight:700;letter-spacing:.07em;border-radius:6px;padding:4px 10px;flex:0 0 auto;white-space:nowrap;color:' + (DARK[fg] ?? fg) + ';background:' + bg + ';border:1px solid ' + bd + ';';
const BTN_PRIMARY = 'display:inline-flex;align-items:center;height:44px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;background:#2F5C97;color:#fff;';
const MODELED = chipC('#5B6B7C', 'rgba(91,107,124,.14)');
const SAMPLE = chipC('#54606C', 'rgba(34,39,46,.09)');
const NUT_INPUT = 'box-sizing:border-box;flex:1 1 130px;min-width:0;min-height:60px;padding:0 16px;border-radius:14px;border:1px solid rgba(34,39,46,.22);font-family:inherit;font-size:21px;font-weight:700;color:#22272E;outline:none;';

/** "2y ago", "18m", "now": how long before the latest check this one was. */
function ageLabel(atMs: number, latestMs: number, last: boolean): [string, string] {
  if (last) return ['now', 'most recent'];
  const months = Math.round((latestMs - atMs) / (30.44 * 86_400_000));
  if (months >= 24) return [`${Math.floor(months / 12)}y ago`, `${Math.floor(months / 12)} years ago`];
  return [`${months}m`, `${months} months ago`];
}

const WORD = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
/** "Five checks over two and a half years." */
function checksSpan(n: number, firstMs: number, lastMs: number): string {
  const count = WORD[n] ?? String(n);
  const head = count.charAt(0).toUpperCase() + count.slice(1) + (n === 1 ? ' check' : ' checks');
  if (n < 2) return `${head}.`;
  const months = Math.round((lastMs - firstMs) / (30.44 * 86_400_000));
  if (months < 12) return `${head} over ${WORD[months] ?? months} month${months === 1 ? '' : 's'}.`;
  const halves = Math.round(months / 6);
  const years = Math.floor(halves / 2);
  const span = (WORD[years] ?? String(years)) + (halves % 2 ? ' and a half' : '') + (years === 1 && !(halves % 2) ? ' year' : ' years');
  return `${head} over ${span}.`;
}

/** When a reading was taken, as the design words it: "just now" inside five minutes, else the date. */
export function readingWhen(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isNaN(t) && now - t < 5 * 60_000) return 'just now';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function MyHomeTab({ c, toast }: { c: MemberCtx; toast: ReactNode }) {
  const plain = useCustomerText();
  const build = useBuildLabels();
  const { d } = c;
  const score = d.score.data?.score ?? null;
  const w = d.wellbeing.data;
  const name = d.me.data ? d.me.data.name.split(' ')[0] : '';
  const [pillar, setPillar] = useState<string | null>(null);
  // Items put on the plan this visit read as the design's "Added to the plan." (hPillarItems note)
  const [fixed, setFixed] = useState<Record<string, true>>({});
  const [wt, setWt] = useState('');
  const [ch, setCh] = useState('');

  const send = (body: Parameters<MemberCtx['act']>[0], after?: () => void) => {
    if (c.readOnly) return;
    c.act(body)
      .then((done) => {
        c.setToast(done);
        d.wellbeing.reload();
        after?.();
      })
      .catch((e: Error) => c.setToast(`Not sent: ${e.message}.`));
  };

  // ── the score ──
  const comp = score?.composite ?? null;
  const grade = score?.grade ?? null;
  const prior = w?.priorComposite ?? null;
  // No change is neither up nor down, and never green.
  const lead = comp == null || prior == null ? '' : comp > prior ? `Up ${comp - prior} points on last month` : comp < prior ? `Down ${prior - comp} points on last month` : 'Same as last month';
  const drivers = (score?.domains ?? []).map((dm) => ({ k: DOMAIN_WORDS[dm.key] ?? dm.label, v: dm.score == null ? null : Math.round(dm.score), note: dm.basis }));
  // A score from fewer than all five measures says how many it rests on.
  const counted = drivers.filter((r) => r.v != null).length;
  const fromWhat = drivers.length && counted < drivers.length ? `from ${counted} of ${drivers.length} measures` : '';

  // ── the memory checks (oldest of the last five first) ──
  const checks = (d.cognitive.data?.assessments ?? []).filter((a) => a.score != null).slice(0, 5).reverse();
  const latestMs = checks.length ? Date.parse(checks[checks.length - 1].created_at) : 0;
  const cog = checks.length ? Number(checks[checks.length - 1].score) : null;
  const drop = checks.length ? Number(checks[0].score) - (cog ?? 0) : 0;
  const mocaColor = drop > 2 ? '#5B6B7C' : '#1F6B52';

  // ── medicines ──
  const medList = d.meds.data?.medications ?? [];
  const doses = w?.doses ?? {};
  const medRows = medList.map((m) => ({ id: String(m.id), name: m.name ?? DASH, dose: [m.dosage, m.frequency].filter(Boolean).join(' · '), why: m.notes ?? '', state: doses[String(m.id)] === 'TAKEN' ? 'TAKEN' : doses[String(m.id)] === 'MISSED' ? 'NOT TAKEN' : 'DUE' }));
  const dueN = medRows.filter((r) => r.state === 'DUE').length;
  const missN = medRows.filter((r) => r.state === 'NOT TAKEN').length;

  // ── weight and strength (same bounds as the Worklist nutrition capture) ──
  const wtN = parseFloat(wt);
  const chN = parseInt(ch, 10);
  const wtOk = wt !== '' && !Number.isNaN(wtN) && wtN >= 60 && wtN <= 500;
  const chOk = ch !== '' && !Number.isNaN(chN) && chN >= 0 && chN <= 40;
  const err = wt === '' && ch === '' ? '' : !wtOk ? 'Weight must be between 60 and 500 lb.' : !chOk ? 'Chair-stand must be between 0 and 40 stands in 30 seconds.' : '';
  const obs = w?.observations ?? [];

  const pillars = (w?.pillars ?? []).map((p) => {
    const done = p.items.filter((i) => i.done).length;
    const pct = p.items.length ? Math.round((done / p.items.length) * 100) : 0;
    return { ...p, done, total: p.items.length, pct };
  });
  const openP = pillars.find((p) => p.name === pillar) ?? null;
  const settled = w?.settled ?? [];
  const waiting = !w && d.wellbeing.error ? plain('Waiting on the member feed (GET /athome/me/:id/wellbeing).') : '';

  return (
    <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:10px;min-width:0;align-items:start;">
      <D css={CARD}>
        <D css="display:flex;align-items:center;gap:18px;flex-wrap:wrap;row-gap:12px;min-width:0;">
          <S css={'display:flex;align-items:center;justify-content:center;width:56px;height:56px;flex:0 0 56px;border-radius:16px;font-size:28px;font-weight:800;color:#FCFAF4;background:' + (grade ? GRADE[grade] ?? '#5B6B7C' : 'rgba(34,39,46,.2)') + ';'}>{grade ?? DASH}</S>
          <S css="flex:1 1 220px;min-width:0;">
            <S css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;">
              <S css="font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;color:#54606C;">How you are holding up</S>
              <BuildLabel><S css={MODELED}>MODELED</S></BuildLabel>
            </S>
            <S css="display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;margin-top:3px;">
              <S css="font-size:38px;font-weight:700;letter-spacing:-.035em;line-height:1;font-variant-numeric:tabular-nums;color:#22272E;">{comp == null ? DASH : String(Math.round(comp))}</S>
              <S css={'font-size:17px;font-weight:700;color:' + (comp != null && prior != null && comp > prior ? '#1F6B52' : '#4A5866') + ';'}>{lead}</S>
            </S>
            {fromWhat && <S css="display:block;font-size:16px;color:#54606C;margin-top:4px;">{fromWhat}</S>}
          </S>
        </D>
        <D css="display:flex;flex-direction:column;gap:7px;margin-top:15px;min-width:0;">
          {drivers.map((r, i) => {
            const col = r.v == null ? '#54606C' : r.v >= 70 ? '#1F6B52' : r.v >= 50 ? '#2F5C97' : '#5B6B7C';
            const bar = r.v == null ? 'transparent' : r.v >= 70 ? '#2F8A6F' : r.v >= 50 ? '#2F5C97' : '#5B6B7C';
            return (
              <S key={r.k} css="display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:4px;min-width:0;">
                <S css="flex:1 1 180px;min-width:0;">
                  <S css="display:block;font-size:17px;font-weight:700;color:#22272E;">{r.k}</S>
                  <S css="display:block;font-size:16px;color:#54606C;">{r.note}</S>
                </S>
                <S css="flex:2 1 140px;min-width:0;height:12px;border-radius:6px;background:rgba(34,39,46,.09);overflow:hidden;">
                  <S css={'display:block;height:100%;width:' + (r.v ?? 0) + '%;border-radius:5px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:' + i * 60 + 'ms;background:' + bar + ';'} />
                </S>
                <S css={'flex:0 0 74px;text-align:right;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:' + col + ';'}>{r.v == null ? 'not counted' : `${r.v}%`}</S>
              </S>
            );
          })}
        </D>
        <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:12px;text-wrap:pretty;">
          {score
            ? 'You against your own last 90 days. Nobody is ranked and this is not compared with other people. Every figure here is modeled from your readings rather than measured directly, and it moves when one of the five moves.'
            : d.score.error
              ? `The score did not load: ${d.score.error.message}`
              : ''}
        </D>
      </D>

      <D css={CARD + 'animation-delay:60ms;'}>
        <D css="display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:6px;min-width:0;">
          <S css="flex:0 0 auto;font-size:20px;font-weight:700;letter-spacing:-.015em;color:#22272E;">Memory checks</S>
          <S css={'flex:0 0 auto;font-size:24px;font-weight:700;letter-spacing:-.025em;font-variant-numeric:tabular-nums;color:' + mocaColor + ';'}>{cog == null ? DASH : `${cog} / 30`}</S>
          <S css={'flex:1 1 160px;min-width:0;font-size:17px;font-weight:700;color:' + mocaColor + ';'}>{!checks.length ? '' : drop > 2 ? `Down ${drop} points since the first check` : `Steady across all ${checks.length}`}</S>
        </D>
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;">
          {checks.length
            ? `${checksSpan(checks.length, Date.parse(checks[0].created_at), latestMs)} Each one is 30 questions, takes about ten minutes, and is done with a person, not Atlas.`
            : 'No memory checks are on file yet. Each one is 30 questions, takes about ten minutes, and is done with a person, not Atlas.'}
        </S>
        <S css="display:flex;align-items:flex-end;gap:10px;height:98px;margin-top:14px;min-width:0;">
          {checks.map((m, i) => {
            const [, title] = ageLabel(Date.parse(m.created_at), latestMs, i === checks.length - 1);
            const last = i === checks.length - 1;
            return (
              <S
                key={m.id}
                title={`${title} · ${m.score} of 30`}
                css={'flex:1 1 0;min-width:12px;height:' + Math.round((Number(m.score) / 30) * 92) + 'px;border-radius:6px 6px 0 0;transform-origin:bottom;animation:growH .55s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 70 + 'ms;background:' + (last ? (drop > 2 ? '#5B6B7C' : '#2F8A6F') : 'rgba(34,39,46,.16)') + ';'}
              />
            );
          })}
        </S>
        <S css="display:flex;gap:10px;margin-top:5px;min-width:0;">
          {checks.map((m, i) => (
            <S key={m.id} css="flex:1 1 0;min-width:0;text-align:center;">
              <S css="display:block;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;">{String(m.score)}</S>
              <S css="display:block;font-size:16px;color:#54606C;">{ageLabel(Date.parse(m.created_at), latestMs, i === checks.length - 1)[0]}</S>
            </S>
          ))}
        </S>
        <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:12px;text-wrap:pretty;">
          {!checks.length
            ? ''
            : drop > 2
              ? 'A drift of this size over two years is worth repeating with your doctor rather than waiting for the next one. It is not a diagnosis and one score never is.'
              : 'No meaningful change across the checks. That is the result you want here.'}
        </S>
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:6px;text-wrap:pretty;">A score of 26 or above is the normal range. Below that is the point to check again, not the point to worry.</S>
      </D>

      <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding:20px 22px;border-radius:18px;background:rgba(91,107,124,.09);border:1px solid rgba(91,107,124,.3);animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:120ms;">
        <D css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;padding-bottom:6px;">
          <S css="flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;color:#54606C;">Where this is heading</S>
          <BuildLabel><S css={MODELED}>MODELED</S></BuildLabel>
        </D>
        <D css="font-size:27px;font-weight:700;letter-spacing:-.025em;line-height:1.16;color:#22272E;text-wrap:pretty;">{w?.forecast?.line ?? 'We will not project this yet.'}</D>
        <D css="font-size:18px;font-weight:700;color:#12503C;line-height:1.45;margin-top:6px;text-wrap:pretty;">{w?.forecast?.move ?? waiting}</D>
        {w?.forecast?.withheld && (
          <D css="margin-top:12px;padding:9px 2px;border-radius:14px;background:rgba(179,38,30,.07);border:1px solid rgba(179,38,30,.3);font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;min-width:0;">{w.forecast.withheldText ?? ''}</D>
        )}
        {w?.forecast && !w.forecast.withheld && (
          <>
            <D css="display:flex;flex-direction:column;gap:6px;margin-top:14px;min-width:0;">
              {w.forecast.paths.map((b) => (
                <S key={b.k} css="display:flex;align-items:center;gap:11px;min-width:0;">
                  <S css="flex:1 1 150px;min-width:0;font-size:16px;font-weight:700;color:#3C454F;">{b.k}</S>
                  <S css="flex:2 1 130px;min-width:0;height:14px;border-radius:7px;background:rgba(34,39,46,.09);overflow:hidden;">
                    <S css={'display:block;height:100%;width:' + b.pct + '%;border-radius:5px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;background:' + PATH(b.tone) + ';'} />
                  </S>
                  <S css="flex:0 0 46px;text-align:right;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;">{`${b.pct}%`}</S>
                </S>
              ))}
            </D>
            <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:8px;text-wrap:pretty;">Your chance of still being in your own home, on each of these three paths.</D>
          </>
        )}
        <D css="font-size:16px;color:#54606C;line-height:1.5;margin-top:8px;text-wrap:pretty;">{build ? 'MODELED' : 'Worked out'} from your own readings against 90 days of history. It is a projection, not a promise, and it changes the moment you do something.</D>
      </D>

      <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding:20px 22px;border-radius:18px;background:rgba(47,92,151,.09);border:1px solid rgba(47,92,151,.32);display:flex;align-items:center;gap:20px;flex-wrap:wrap;row-gap:12px;animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:70ms;">
        <S css="flex:1 1 260px;min-width:0;">
          <S css="display:block;font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#54606C;">The one thing to sort this month</S>
          <S css="display:block;font-size:24px;font-weight:700;letter-spacing:-.02em;line-height:1.18;color:#22272E;margin-top:5px;text-wrap:pretty;">{w?.oneThing?.title ?? DASH}</S>
          <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:6px;text-wrap:pretty;">{w?.oneThing?.why ?? waiting}</S>
          <S css="display:block;font-size:16px;color:#54606C;margin-top:6px;">{w?.oneThing ? `Owner: ${w.oneThing.owner} · you do not have to arrange it` : ''}</S>
        </S>
        {w?.oneThing && (
          <Act
            css="flex:0 0 auto;display:inline-flex;align-items:center;min-height:60px;padding:0 24px;border-radius:15px;font-size:18px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#2F5C97;transition:background .16s ease;"
            onClick={() =>
              c.openSheet({
                kicker: 'THIS MONTH',
                title: w.oneThing!.title,
                glyph: '✓',
                color: '#2F5C97',
                body: 'We pick one thing a month, so there is never a list. This one was chosen because it moves your forecast further than anything else you could do.',
                rows: facts([['Who arranges it', w.oneThing!.owner], ['What it costs you', 'nothing; it is inside your plan'], ['How long', 'about a week']]),
                primary: 'Yes, arrange it',
                onPrimary: () => c.act({ kind: 'one_thing', target: w.oneThing!.title }),
              })
            }
          >
            {w.oneThing.button}
          </Act>
        )}
      </D>

      {toast}

      <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);">
        <D css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;">
          <S css="flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;color:#22272E;">The five things behind that score</S>
          <BuildLabel><S css={MODELED}>MODELED</S></BuildLabel>
        </D>
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.45;margin-top:3px;text-wrap:pretty;">Each one against your own last 90 days, never against other people. Anything marked SAMPLE is modeled rather than measured.</S>
        <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:10px;margin-top:11px;min-width:0;">
          {(w?.trackers ?? []).map((t, i) => {
            const col = TRK[t.tone];
            const max = Math.max(...t.points, 1);
            return (
              <D key={t.k} css={'box-sizing:border-box;min-width:0;padding:14px 16px;border-radius:16px;border:1px solid ' + (col === '#B3261E' ? 'rgba(179,38,30,.34)' : 'rgba(34,39,46,.12)') + ';animation:riseIn .46s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 55 + 'ms;'}>
                <S css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;min-width:0;">
                  <S css="flex:1 1 0;min-width:0;font-size:18px;font-weight:700;color:#22272E;">{t.k}</S>
                  <S css="flex:0 0 auto;font-size:19px;font-weight:700;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:#22272E;">{t.value}</S>
                  {t.sample && <BuildLabel><S css={SAMPLE}>SAMPLE</S></BuildLabel>}
                </S>
                <S css="display:flex;align-items:flex-end;gap:3px;height:44px;margin-top:9px;min-width:0;">
                  {t.points.map((p, pi) => (
                    <S key={pi} title={`${(7 - pi) * 12} days ago`} css={'flex:1 1 0;min-width:3px;height:' + Math.max(4, Math.round((p / max) * 40)) + 'px;border-radius:3px 3px 0 0;transform-origin:bottom;animation:growH .55s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + (i * 60 + pi * 30) + 'ms;background:' + (pi === t.points.length - 1 ? col : 'rgba(34,39,46,.16)') + ';'} />
                  ))}
                </S>
                <S css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:8px;">
                  <S css={chipC(col, col + '1F')}>{t.direction}</S>
                  <S css="font-size:16px;color:#54606C;">90 days {'·'} your own range</S>
                </S>
                <S css="display:block;font-size:16px;color:#3C454F;line-height:1.45;margin-top:7px;text-wrap:pretty;">{t.meaning}</S>
                <S css="display:block;font-size:16px;font-weight:700;color:#1F6B52;margin-top:6px;text-wrap:pretty;">{t.unlocks}</S>
              </D>
            );
          })}
        </D>
        <D css="display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap;row-gap:12px;margin-top:11px;padding:16px 18px;border-radius:18px;background:rgba(47,92,151,.08);border:1px solid rgba(47,92,151,.28);min-width:0;">
          <S css="flex:1 1 100%;min-width:0;">
            <S css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;">
              <S css="flex:0 0 auto;font-size:20px;font-weight:700;letter-spacing:-.015em;color:#22272E;">Weight and strength</S>
              {obs.length === 0 && <BuildLabel><S css={SAMPLE}>SAMPLE</S></BuildLabel>}
            </S>
            <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;">
              Two numbers, once a week, written to your care record. A 5% weight drop in 30 days is the point a nutrition review is triggered, and how many times you can stand from a chair in 30 seconds is the best single measure of whether a fall is coming.
            </S>
          </S>
          <S css="flex:1 1 150px;min-width:0;">
            <S css="display:block;font-size:16px;font-weight:700;color:#3C454F;margin-bottom:5px;">Weight, in pounds</S>
            <input type="text" value={wt} onChange={(e) => setWt(e.target.value)} placeholder="148" aria-label="Weight, in pounds" disabled={c.readOnly} style={sx(NUT_INPUT)} />
          </S>
          <S css="flex:1 1 150px;min-width:0;">
            <S css="display:block;font-size:16px;font-weight:700;color:#3C454F;margin-bottom:5px;">Stands in 30 seconds</S>
            <input type="text" value={ch} onChange={(e) => setCh(e.target.value)} placeholder="9" aria-label="Stands in 30 seconds" disabled={c.readOnly} style={sx(NUT_INPUT)} />
          </S>
          <Act
            css={'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;min-height:60px;padding:0 26px;border-radius:15px;font-size:18px;font-weight:700;text-decoration:none;' + (wtOk && chOk ? 'color:#FCFAF4;background:#2F5C97;' : 'color:#54606C;background:rgba(34,39,46,.08);border:1px solid rgba(34,39,46,.16);')}
            onClick={() => send({ kind: 'observation', value: JSON.stringify({ lb: wtN, chairStands: chN }) }, () => { setWt(''); setCh(''); })}
            disabled={c.readOnly || !(wtOk && chOk)}
            reason={c.readOnly ? c.reason : 'Enter a weight and a chair-stand count first.'}
          >
            Record it
          </Act>
          {err && <S css="flex:1 1 100%;min-width:0;font-size:16px;font-weight:700;color:#B3261E;text-wrap:pretty;">{err}</S>}
          {obs.length === 0 && <S css="flex:1 1 100%;min-width:0;font-size:16px;color:#54606C;line-height:1.5;text-wrap:pretty;">No weight has been measured yet. Record one here and Aithora uses it instead of an estimate.</S>}
          {obs.length > 0 && (
            <S css="flex:1 1 100%;min-width:0;">
              {obs.slice().reverse().map((o, i) => (
                <S key={i} css="display:flex;align-items:baseline;gap:10px;padding:6px 0;border-top:1px solid rgba(34,39,46,.1);min-width:0;">
                  <S css="flex:1 1 0;min-width:0;font-size:17px;font-weight:700;color:#22272E;">{`${Math.round(o.lb)} lb · ${o.chairStands} stands`}</S>
                  <S css="flex:0 0 auto;font-size:16px;color:#54606C;">{`${o.by === d.me.data?.name ? 'you' : o.by} · ${readingWhen(o.at)}`}</S>
                </S>
              ))}
            </S>
          )}
        </D>
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:9px;text-wrap:pretty;">{build ? 'MODELED' : 'Worked out'} against your own baseline. What you log here is the evidence Aithora uses to claim assessments and allowances back on your behalf, which is why the plan costs you nothing.</S>
        <D css="margin-top:11px;padding:16px 18px;border-radius:18px;background:#FCFAF4;border:1px solid rgba(34,39,46,.12);min-width:0;">
          <S css="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;min-width:0;">
            <S css="flex:0 0 auto;font-size:20px;font-weight:700;letter-spacing:-.015em;color:#22272E;">Your medicines today</S>
            <S css="flex:1 1 140px;min-width:0;font-size:16px;color:#54606C;">{medRows.length ? `${dueN} still to take of ${medRows.length}${missN ? ` · ${missN} recorded as not taken` : ''}` : ''}</S>
          </S>
          {medRows.map((m) => {
            const v = m.state;
            return (
              <D key={m.id} css="display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:10px;min-width:0;padding:13px 0;border-top:1px solid rgba(34,39,46,.1);">
                <S css="flex:1 1 180px;min-width:0;">
                  <S css="display:flex;align-items:center;gap:9px;flex-wrap:wrap;min-width:0;">
                    <S css="font-size:19px;font-weight:700;color:#22272E;">{m.name}</S>
                    <S css={chipC(v === 'TAKEN' ? '#1F6B52' : v === 'NOT TAKEN' ? '#B3261E' : '#5B6B7C', v === 'TAKEN' ? 'rgba(47,138,111,.14)' : v === 'NOT TAKEN' ? 'rgba(179,38,30,.12)' : 'rgba(91,107,124,.13)')}>{v}</S>
                  </S>
                  <S css="display:block;font-size:16px;color:#3C454F;margin-top:2px;">{m.dose}</S>
                  <S css="display:block;font-size:16px;color:#54606C;">{m.why}</S>
                </S>
                <Act css={'flex:1 1 120px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:56px;padding:0 18px;border-radius:14px;font-size:17px;font-weight:700;text-decoration:none;' + (v === 'TAKEN' ? 'color:#FCFAF4;background:#1F6B52;' : 'color:#1F6B52;border:1px solid rgba(47,138,111,.45);')} onClick={() => send({ kind: 'dose', target: m.id, value: 'TAKEN' })} disabled={c.readOnly} reason={c.reason}>
                  I took it
                </Act>
                <Act css={'flex:0 1 130px;min-width:0;display:inline-flex;align-items:center;justify-content:center;min-height:56px;padding:0 16px;border-radius:14px;font-size:17px;font-weight:700;text-decoration:none;' + (v === 'NOT TAKEN' ? 'color:#FCFAF4;background:#B3261E;' : 'color:#B3261E;border:1px solid rgba(179,38,30,.4);')} onClick={() => send({ kind: 'dose', target: m.id, value: 'MISSED' })} disabled={c.readOnly} reason={c.reason}>
                  Not yet
                </Act>
              </D>
            );
          })}
          {medRows.length === 0 && (
            <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:8px;text-wrap:pretty;">{`No medicines are on file for ${name}. Once the care team adds them they appear here, one row a dose, exactly as the nurse sees them.`}</S>
          )}
          <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:10px;text-wrap:pretty;">Same list your care team works from. Saying you have not taken one is never held against you.</S>
        </D>
      </D>

      <D css="grid-column:1/-1;box-sizing:border-box;min-width:0;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);">
        <S css="display:block;font-size:16px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#22272E;">Already settled</S>
        <S css="display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:0 22px;margin-top:4px;min-width:0;">
          {settled.map((x) => (
            <S key={x.k} css="display:flex;align-items:baseline;gap:10px;min-width:0;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);">
              <S css="flex:0 0 auto;font-size:16px;font-weight:700;color:#1F6B52;">{'✓'}</S>
              <S css="flex:1 1 0;min-width:0;font-size:17px;color:#22272E;">{x.k}</S>
              <S css="flex:0 0 auto;font-size:16px;color:#54606C;white-space:nowrap;">{x.v}</S>
            </S>
          ))}
        </S>
        {settled.length === 0 && <S css="display:block;font-size:17px;color:#3C454F;line-height:1.5;margin-top:8px;text-wrap:pretty;">{`Nothing is settled yet for ${name}. The first item appears here as soon as one is.`}</S>}
        <S css="display:block;font-size:16px;color:#54606C;line-height:1.5;margin-top:9px;text-wrap:pretty;">This list only grows. Nothing here expires and nothing here is scored.</S>
      </D>
      {pillars.map((p) => {
        const col = p.pct >= 85 ? '#1F6B52' : p.pct >= 60 ? '#5B6B7C' : '#B3261E';
        const gaps = p.items.filter((x) => !x.done);
        const on = pillar === p.name;
        return (
          <a
            key={p.name}
            href="#"
            aria-expanded={on}
            onClick={(e) => {
              e.preventDefault();
              setPillar(on ? null : p.name);
            }}
            style={sx('display:block;text-decoration:none;color:inherit;border-radius:14px;padding:14px 16px;border:1px solid ' + (on ? 'rgba(47,92,151,.45);' : 'rgba(34,39,46,.12);'))}
          >
            <S css="display:flex;align-items:center;gap:10px;">
              <S css="flex:1 1 0;min-width:0;font-size:17px;font-weight:700;color:#22272E;">{p.name}</S>
              <S css={mchip(col, col + '26', col + '3d')}>{`${p.done} of ${p.total}`}</S>
              <S css="font-size:16px;color:#54606C;">{on ? '−' : '+'}</S>
            </S>
            <S css="display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.09);margin-top:10px;position:relative;">
              <S css={'position:absolute;left:0;top:0;bottom:0;border-radius:3px;width:' + Math.max(3, p.pct) + '%;background:' + col + ';'} />
            </S>
            <S css="display:block;font-size:16px;color:#54606C;margin-top:8px;line-height:1.45;">{gaps.length === 0 ? 'Nothing outstanding.' : gaps[0].name + (gaps.length > 1 ? ` + ${gaps.length - 1} more` : '')}</S>
          </a>
        );
      })}
      {openP && (
        <D css="box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;">
          <D css="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:4px;">
            <S css="font-size:17px;font-weight:700;">{openP.name}</S>
            <S css={mchip('#2F5C97', 'rgba(79,138,214,.2)', 'rgba(47,92,151,.34)')}>{`${openP.done} of ${openP.total} in place`}</S>
            <S css="flex:1;" />
            <Act css="font-size:16px;color:#54606C;text-decoration:none;padding:8px;" onClick={() => setPillar(null)}>
              Close
            </Act>
          </D>
          <D css="font-size:16px;color:#54606C;line-height:1.5;margin-bottom:12px;text-wrap:pretty;">{openP.note}</D>
          <D css="display:flex;flex-direction:column;gap:8px;">
            {openP.items.map((i) => (
              <D key={i.id} css={'border-radius:10px;padding:11px 13px;background:#FBF8F1;border:1px solid ' + (i.done ? 'rgba(47,138,111,.24);' : 'rgba(91,107,124,.32);')}>
                <S css="display:flex;align-items:flex-start;gap:10px;">
                  <S css={'display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:5px;font-size:16px;font-weight:700;margin-top:1px;' + (i.done ? 'background:rgba(47,138,111,.2);color:#12503C;' : 'background:rgba(34,39,46,.07);border:1px solid rgba(34,39,46,.2);color:transparent;')}>{i.done ? '✓' : ''}</S>
                  <S css="flex:1 1 0;min-width:0;">
                    <S css="display:block;font-size:16px;font-weight:600;color:#22272E;">{i.name}</S>
                    <S css="display:block;font-size:16px;color:#54606C;margin-top:3px;line-height:1.45;text-wrap:pretty;">{fixed[i.id] ? 'Added to the plan.' : i.note}</S>
                  </S>
                </S>
                {!i.done && i.action && (
                  <Act css={BTN_PRIMARY + 'margin-top:9px;margin-left:27px;'} onClick={() => send({ kind: 'pillar', target: i.id }, () => setFixed((f) => ({ ...f, [i.id]: true })))} disabled={c.readOnly} reason={c.reason}>
                    {i.action}
                  </Act>
                )}
              </D>
            ))}
          </D>
        </D>
      )}
      {/* Restored from production (not in the design): the health watch and the medication review. */}
      <HealthWatch personId={c.personId} orgId={c.orgId} readOnly={c.readOnly} reason={c.reason} />
    </D>
  );
}
